import React, { useContext, useEffect } from 'react'
import useMemberTable from '../context/MemberTableContext'
import MicroModal from 'micromodal'
import { ColumnDef, ColumnFiltersState, SortingState } from '@tanstack/react-table'
import { useParams } from 'react-router-dom'
import TableCheckbox from '../../../../common/checkbox/TableCheckbox'
import { getExportHeaders } from '../../ReportingGroup/types/tableHeaders'
import TanstackContent from '../../Table/Tanstack/TanstackContent'
import { TeamContext } from '../../MenuTabs/MenuTabs'
import { JoinRequestTableData, JoinRequestTableType } from '../data/JoinRequestsData'
import Button from '../../../../common/button/Button'

const JoinRequestsTable = ({ joinRequestsData }) => {

  const [ columnFilters, setColumnFilters ] = React.useState<ColumnFiltersState>([])
  const { setUseHeader } = useMemberTable()
  const [ rowSelection, setRowSelection ] = React.useState({})
  const [ globalFilter, setGlobalFilter ] = React.useState('')
  const [ sorting, setSorting ] = React.useState<SortingState>([])
  const [ data, setData ] = React.useState( () => JoinRequestTableData( joinRequestsData ) )
  const { refetchJoinRequests } = useMemberTable()

  const params = useParams()
  const teamId = params.team_id ?? 0

  useEffect(() => {
    MicroModal.init()
    setUseHeader([ 'all' ])
  }, [])

  const columns = React.useMemo<ColumnDef<JoinRequestTableType>[]>(
    () => [
      {     
        id: 'select',
        header: ({ table }) => (
          <TableCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <TableCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        id: 'columns',
        columns: [
          {
            id: 'inviteID',
            accessorKey: 'inviteID',
            prettyName: 'Invite ID',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>INVITE ID</span>,
          },
          {
            id: 'email',
            accessorKey: 'email',
            prettyName: 'Email',
            show: true,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>EMAIL</span>,
          },
          {
            id: 'dateReceived',
            accessorKey: 'dateReceived',
            prettyName: 'Date Received',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>DATE RECEIVED</span>,
          },
          {
            id: 'dateAccepted',
            accessorKey: 'dateAccepted',
            prettyName: 'Date Accepted',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>DATE ACCEPTED</span>,
          },
          {
            id: 'status',
            accessorKey: 'status',
            prettyName: 'Status',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>STATUS</span>,
          },
          {
            id: 'actions',
            accessorKey: 'actions',
            enableSorting: false,
            prettyName: 'Actions',
            cell: (info) => (
              <Actions info={ info.row.original } />
            ),
            footer: props => props.column.id,
            header: () => <span>ACTIONS</span>,
            meta: {
              className: "msync-custom-member-action-header",
            },
          },
        ]
      }
    ],
    []
  )

  const exportHeaders = getExportHeaders('team-members')
  const { teamData } = useContext(TeamContext)

  const AcceptOrgJoinRequest = async ( inviteID ) => {
    const currentRestUrl = window.group_dashboard.base
    const nonce = window.group_dashboard.nonce

    const url = currentRestUrl + 'membersync/licensing/v1/org/' + teamId + '/invite/' + inviteID + '/accept'
  
    await fetch(url, {
        method: 'GET',
        headers:{
          'X-WP-Nonce': nonce,
          'Content-type': 'application/json'
        }
    }).then(resp => {
        return resp.json()
    })
    .then((json) => {
    })
  }

  const DeleteOrgJoinRequest = async ( inviteID ) => {
    const currentRestUrl = window.group_dashboard.base
    const nonce = window.group_dashboard.nonce

    const url = currentRestUrl + 'membersync/licensing/v1/invite/' + inviteID
  
    await fetch(url, {
        method: 'DELETE',
        headers:{
          'X-WP-Nonce': nonce,
          'Content-type': 'application/json'
        }
    }).then(resp => {
        return resp.json()
    })
    .then((json) => {
    })
  }

  const Actions = ({ info }) => {
    return (
      <div className='msync-table-action-column'>
        <Button
          className={info.status === 'Pending' ? 'msync-button' : 'msync-button--is-theme-destructive'} 
          aria-label={info.status === 'Pending' ? 'Pending request' : 'Delete request'} 
          onClick={ async ()=> {
            info.status === 'Pending' ? 
              await AcceptOrgJoinRequest( info.inviteID ) : 
              await DeleteOrgJoinRequest( info.inviteID )

            refetchJoinRequests()
          }}
        >
          {info.status === 'Pending' ? 'Accept' : 'Delete'}
        </Button>
      </div>
    )
  }

  return (
    <>
      <TanstackContent
        data={ data }
        columns={ columns }
        rowSelection={ rowSelection }
        setRowSelection={ setRowSelection }
        sorting={ sorting }
        setSorting={ setSorting }
        globalFilter={ globalFilter }
        setGlobalFilter={ setGlobalFilter }
        exportHeaders={ exportHeaders }
        from='Join Requests Table'
        context='Join Requests'
        hideFilters={ true }
        columnFilters={ columnFilters }
        setColumnFilters={ setColumnFilters }
      >
        <></>
      </TanstackContent>
    </>
  )
}

export default JoinRequestsTable
